import './Toast.scss';

import SnackIcon from 'design-system/SnackBar/SnackIcon/SnackIcon';
import { Component, ReactNode } from 'react';
import type { NotificationActionOld, NotificationType } from 'types/common/notification';

import Button from '../../Button/Button';

type ToastState = { onLeave: boolean };

export type ToastProps = {
  timeout: number;
  removeAfter: number;
  containerSize: 'medium' | 'tiny';
  type: NotificationType;
  message: ReactNode;
  exitDirection: 'bottom';
  close: (id: string) => void;
  id: string;
  title?: string;
  action?: NotificationActionOld;
};

class Toast extends Component<ToastProps, ToastState> {
  static defaultProps: Required<Pick<ToastProps, 'timeout' | 'removeAfter' | 'containerSize' | 'type' | 'message' | 'exitDirection'>> = {
    timeout: 500,
    removeAfter: 5000,
    containerSize: 'medium',
    type: 'error',
    message: '',
    exitDirection: 'bottom',
  };

  private removeAfterTimeout: NodeJS.Timeout | undefined;

  private closeTimeout: NodeJS.Timeout | undefined;

  constructor(props: ToastProps) {
    super(props);
    this.state = {
      onLeave: false,
    };
    this.handleHoverOn = this.handleHoverOn.bind(this);
    this.handleHoverOff = this.handleHoverOff.bind(this);
    this.onClose = this.onClose.bind(this);
  }

  componentDidMount() {
    const { removeAfter } = this.props;

    if (removeAfter) {
      this.removeAfterTimeout = setTimeout(() => {
        this.onClose();
      }, removeAfter);
    }
  }

  componentWillUnmount() {
    clearTimeout(this.closeTimeout);
    clearTimeout(this.removeAfterTimeout);
  }

  handleHoverOn = () => {
    clearTimeout(this.removeAfterTimeout);
  };

  handleHoverOff = () => {
    const { removeAfter } = this.props;
    this.removeAfterTimeout = setTimeout(() => {
      this.onClose();
    }, removeAfter);
  };

  onClose = () => {
    const { close, id, timeout } = this.props;
    this.setState({
      onLeave: true,
    });

    this.closeTimeout = setTimeout(() => {
      close(id);
    }, timeout);
  };

  render() {
    const { onLeave } = this.state;

    const { message, type, timeout, exitDirection, title, action, containerSize } = this.props;
    const style = onLeave ? { animation: `exit-${exitDirection} ${timeout + 100}ms ease` } : undefined;

    return (
      <div
        className={`toast-component toast-component ${type} ${containerSize}`}
        style={style}
        onMouseEnter={this.handleHoverOn}
        onMouseLeave={this.handleHoverOff}
      >
        <span className="emoji" role="img" aria-label={type}>
          <SnackIcon type={type} />
        </span>
        {(containerSize !== 'tiny' || title) && <h4 className="title">{title}</h4>}
        <p role="alert" className="content">
          {message}
        </p>
        {action && (
          <div className="inline-form-item align-end m-t-12">
            <Button
              whiteInverted
              large
              content={action.label}
              onClick={() => {
                action.action();
                this.onClose();
              }}
            />
          </div>
        )}

        <span className="close-icon" role="button" tabIndex={-1} aria-label="Close" onClick={this.onClose}>
          &times;
        </span>
      </div>
    );
  }
}

export default Toast;
